import { Component, Prop, Vue } from "vue-property-decorator";
import { Drawer, Input, Button, Icon, Form, Spin, List, Row, Col, Select, Modal } from "ant-design-vue";
import {
	CommonEntityResponseModel,
	CommonIntentDetailsModel,
	CommonIntentListModel,
	CommonIntentUtteranceModel,
	ConversationTemplateService
} from "@/services/conversation-templates";
import { CommonIntentsFormLocale, CommonLocale, IntentLocale } from "@/locales/localeid";
import { fmtMsg, Action, ActionItem, MessageHelper, NotificationType, ServerExceptionCode } from "commonui";
import { CommonIntentUtteranceStatus } from "@/util/enum";
import { isNull, isUndefined } from "lodash";
import { replaceMultipleWhiteSpace } from "@/util/func";
import { CLUValidations, CommonRegex } from "@/util/constants";

const utteranceMaxLen = 500;
const patternedUtterancMaxLen = 400;
const utteranceMinLen = 1;
const nameMaxLen = 50;
interface IntentItemEditModel {
	id: number;
	value: string;
	index: number;
	isInEditMode: boolean;
	commonEntityName?: string;
	commonEntityId?: number;
	isValid: boolean;
	validationMessage: string;
}
@Component({
	components: {
		Drawer,
		TextArea: Input.TextArea,
		Button,
		Icon,
		Input,
		Spin,
		Form,
		FormItem: Form.Item,
		Action,
		ActionItem,
		List,
		ListItem: List.Item,
		Row,
		Col,
		Select,
		Option: Select.Option
	}
})
export default class CommonIntentsForm extends Vue {
	@Prop({ default: 0 }) intentId!: number;
	@Prop() onCommonIntentsFormClose!: (reloadRequired: boolean) => void;
	@Prop({ default: false }) isDrawerVisible!: boolean;
	@Prop({ default: [] }) commonIntents!: CommonIntentListModel[];
	public loading = false;
	private templateService = ConversationTemplateService();
	public locale = CommonIntentsFormLocale;
	public commonLocale = CommonLocale;
	public intentLocale = IntentLocale;
	public format = fmtMsg;
	private intentData?: CommonIntentDetailsModel;
	public commonIntentForm;
	public utterances: IntentItemEditModel[] = [];
	public commonEntities: CommonEntityResponseModel[] = [];
	public selectedEntityId: number | null = null;
	public editUtteranceText: string = "";
	public utteranceInvalidMessage: string = "";
	public isAddButtonDisabled = true;
	public addUtteranceText: string = "";
	private isRemoving: boolean = false;
	public enableSaveButton: boolean = false;

	onEditCommonIntent() {
		this.enableSaveButton = true;
	}

	created() {
		this.getEntities();
		this.commonIntentForm = this.$form.createForm(this, { name: "common-intent-form" });
	}

	getEntities() {
		this.loading = true;
		this.templateService
			.getcommonEntitiesList()
			.then((data) => {
				this.commonEntities = data;
				this.loading = false;
				if (this.intentId > 0) {
					this.getIntentData();
				}
			})
			.catch(() => {
				this.loading = false;
			});
	}

	setFormData(intentData?: CommonIntentDetailsModel) {
		if (intentData) {
			this.commonIntentForm.getFieldDecorator("name", {
				initialValue: intentData.name,
				preserve: true
			});

			const utterances: IntentItemEditModel[] = [];
			let current = 0;
			const utteranceWithEntityId = intentData.commonUtterances.find(
				(u) => !isUndefined(u.commonEntityId) && !isNull(u.commonEntityId)
			);

			if (utteranceWithEntityId && utteranceWithEntityId.commonEntityId) {
				this.selectedEntityId = utteranceWithEntityId.commonEntityId;
			}

			intentData.commonUtterances.forEach((e) => {
				utterances.push({
					id: e.id,
					index: current,
					isInEditMode: false,
					value: e.value,
					commonEntityName: e.commonEntityName,
					commonEntityId: e.commonEntityId,
					isValid: true,
					validationMessage: ""
				});
				current = current + 1;
			});

			this.utterances = utterances;
		}
	}

	get isSaveButtonDisable() {
		return this.utterances.filter((e) => e.isInEditMode).length > 0;
	}

	public addUtterenceValueChange(e: Event & any) {
		let value = e.target.value;

		this.isAddButtonDisabled = !e.target || !replaceMultipleWhiteSpace(value).trim();

		if (!this.isRemoving && value.slice(-1) === "{" && this.selectedEntityId) {
			const entityName = this.commonEntities.find((e) => e.id === this.selectedEntityId)?.name.trim();

			if (entityName) {
				value = `${value}${entityName}}`;
				this.addUtteranceText = value;
			}
		}
		this.isRemoving = false;
	}

	onKeyDown(e: KeyboardEvent) {
		if (e.key === "Backspace" || e.key === "Delete" || (e.key === "x" && e.ctrlKey)) {
			this.isRemoving = true;
		}
	}

	get drawerTitle() {
		return this.intentId > 0
			? this.format(this.locale.CommonIntentsFormEditTitle)
			: this.format(this.locale.CommonIntentsFormCreateTitle);
	}

	getIntentData() {
		this.loading = true;
		this.templateService
			.getCommonIntentDetails(this.intentId)
			.then((data) => {
				this.intentData = data;
				this.setFormData(data);
				this.loading = false;
			})
			.catch(({ error_code, error_description }) => {
				this.showErrorMessage({ error_code, error_description });
				this.loading = false;
				this.onCommonIntentsFormClose(false);
			});
	}

	saveEditIntentData(intentDetails: CommonIntentDetailsModel) {
		this.loading = true;
		this.templateService
			.saveUpdateCommonIntent(intentDetails)
			.then((data) => {
				this.loading = false;
				MessageHelper.Message(
					NotificationType.Success,
					this.format(this.locale.CommonIntentsFormSavedSuccessMessage)
				);
				this.onCommonIntentsFormClose(true);
			})
			.catch((response) => {
				if (response.body) {
					const { error_code, error_description } = response.body;
					this.showErrorMessage({ error_code, error_description });
				}
				this.loading = false;
			});
	}

	onAddEntityUtterance() {
		if (this.isSaveButtonDisable) {
			return;
		}
		const maxIndex =
			this.utterances.length > 0 ? this.utterances.reduce((p, c) => (p.index > c.index ? p : c)).index : 0;
		const newUtterance: IntentItemEditModel = {
			id: 0,
			isValid: true,
			index: maxIndex + 1,
			value: "",
			isInEditMode: true,
			commonEntityName: "",
			validationMessage: ""
		};
		this.utterances.push(newUtterance);
		this.scrollItemsArea();
	}

	public addUtterence() {
		if (this.addUtteranceText.length) {
			const maxOrder = this.utterances.length;
			const entity = this.commonEntities.find((e) => e.id === this.selectedEntityId);
			this.utterances = [
				...this.utterances,
				{
					id: 0,
					value: replaceMultipleWhiteSpace(this.addUtteranceText),
					index: maxOrder + 1,
					isInEditMode: false,
					commonEntityName: this.selectedEntityId ? entity?.name : "",
					commonEntityId: this.selectedEntityId ? entity?.id : undefined,
					isValid: true,
					validationMessage: ""
				}
			];
			this.utteranceInvalidMessage = this.validateUtterances();
			this.addUtteranceText = "";
			this.isAddButtonDisabled = true;
			this.onEditCommonIntent();
		}
	}

	get isEntitySelectDisable() {
		return (
			(this.selectedEntityId && this.utterances.filter((e) => e.commonEntityId).length > 0) ||
			this.isSaveButtonDisable
		);
	}

	handleChange(value) {
		this.selectedEntityId = value;
		this.onEditCommonIntent();
	}

	onEditUtterance(index: number) {
		if (this.isSaveButtonDisable) {
			return;
		}

		let currentItem: any = null;
		this.utterances = this.utterances.map((e) => {
			if (e.index === index) {
				currentItem = e;
				e.isInEditMode = true;
			}
			return e;
		});

		if (currentItem) {
			this.editUtteranceText = currentItem.value;
			this.utteranceInvalidMessage = this.validateUtterances();
			this.validateUtterance(currentItem.value, currentItem, index);
		}
	}

	// Checks for invalid conversation tag, returns true if valid, false otherwise.
	isEntityValid(regex, text) {
		const result = regex.exec(text);

		if (result && result[0]) {
			var matchText = result[0];

			if (this.selectedEntityId) {
				const currentEntity = this.commonEntities.find((c) => c.id == this.selectedEntityId);

				if (currentEntity && `{${currentEntity.name}}` === matchText) {
					return this.isEntityValid(regex, text); // recursively validate all entities.
				} else {
					return false;
				}
			} else {
				return false;
			}
		}

		return true;
	}

	// Checks if count of opening tags '{' match is equal to closing tags '}', true if matched, false otherwise.
	isEntityCountValid(text) {
		const openEntityRegex = /\{/g;
		const closeEntityRegex = /\}/g;
		let openCount = 0;
		let closeCount = 0;
		let openResult;
		let closeResult;

		while ((openResult = openEntityRegex.exec(text))) {
			openCount++;
		}

		while ((closeResult = closeEntityRegex.exec(text))) {
			closeCount++;
		}

		if ((openCount === 0 && closeCount === 0) || (openCount === 1 && closeCount === 1)) {
			return true;
		} else {
			false;
		}
	}

	uniqueTextValidator(rule: any, value: string, callback: (msg?: string) => void) {
		if (!value || value === "") {
			callback();
			return;
		}

		const data: CommonIntentListModel[] =
			this.intentId > 0 ? this.commonIntents.filter((c) => c.id !== this.intentId) : this.commonIntents;

		if (
			data.some(
				(d) => d.name.trim().toLocaleLowerCase() === replaceMultipleWhiteSpace(value).trim().toLocaleLowerCase()
			)
		) {
			callback(fmtMsg(this.locale.CommonIntentsFormNameAlreadyExistMessage));
			return;
		}

		callback();
	}

	intentNameValidator(rule: any, value: string, callback: (msg?: string) => void) {
		const isInvalid = CLUValidations.invalidIntentNameRegex.test(value);
		if (isInvalid) {
			callback(fmtMsg(this.locale.CommonIntentsFormNameNotValidMessage));
			return;
		}
		callback();
	}

	get maxUtteranceLenValue() {
		return utteranceMaxLen;
	}

	get nameMaxValue() {
		return nameMaxLen;
	}

	showUtteranceLengthMessage(utterance: IntentItemEditModel) {
		return utterance.value.length > utteranceMaxLen;
	}

	onUtteranceChange(e: Event & { target: { value: any } }, index: number) {
		const currentItem = this.utterances.find((e) => e.index === index);
		let value: any = e.target.value;

		if (currentItem) {
			if (value && value.length) {
				if (!currentItem.commonEntityId) {
					if (!this.isRemoving && value.slice(-1) === "{" && this.selectedEntityId) {
						const entity = this.commonEntities.find((e) => e.id === this.selectedEntityId);
						if (entity) {
							this.utterances = this.utterances.map((e) => {
								if (e.index === index) {
									value = `${this.editUtteranceText}${entity.name}}`;
									this.editUtteranceText = value;
									e.commonEntityName = entity.name;
									e.commonEntityId = entity.id;
								}
								return e;
							});
						}
					}
					this.isRemoving = false;
				}

				this.utterances = this.utterances.map((e) => {
					if (e.index === index) {
						if (!e.value.includes("{")) {
							e.commonEntityName = "";
							e.commonEntityId = undefined;
						}
						e.isValid = true;
						e.validationMessage = "";
					}
					return e;
				});
				this.utteranceInvalidMessage = this.validateUtterances();
			} else {
				this.utteranceInvalidMessage = this.validateUtterances();

				this.utterances = this.utterances.map((e) => {
					if (e.index === index) {
						e.commonEntityName = "";
						e.commonEntityId = undefined;
					}
					return e;
				});
			}
		}
	}

	validateUtterance(value: string, currentItem, index) {
		const regex = /\{.*?\}/g;

		if (value.trim() === "") {
			this.markUtteranceInvalid(this.format(this.locale.CommonIntentsFormUtteranceValueRequiredMessage), index);
			return false;
		}

		const isEntityValid = this.isEntityValid(regex, value);

		if (!isEntityValid) {
			this.markUtteranceInvalid(
				this.format(this.locale.CommonIntentsFormUtteranceEntityAssociationMessage),
				index
			);
			return;
		}

		const isEntityCountValid = this.isEntityCountValid(value);
		if (!isEntityCountValid) {
			this.markUtteranceInvalid(
				this.format(this.locale.CommonIntentsFormUtteranceEntityAssociationMessage),
				index
			);
			return false;
		}

		if (value.length > utteranceMaxLen) {
			this.markUtteranceInvalid(
				this.format(this.locale.CommonIntentsFormUtteranceLengthMessage, { len: utteranceMaxLen }),
				index
			);
			return false;
		}

		if (value.length < utteranceMinLen) {
			this.markUtteranceInvalid(
				this.format(this.locale.CommonIntentsFormUtteranceMinLengthMessage, { len: utteranceMinLen }),
				index
			);
			return false;
		}

		const similarItems = this.utterances.filter(
			(e) =>
				replaceMultipleWhiteSpace(e.value).toLocaleLowerCase().trim() ===
				replaceMultipleWhiteSpace(currentItem.value).toLocaleLowerCase().trim()
		);

		if (similarItems.length > 1) {
			this.markUtteranceInvalid(
				this.format(this.locale.CommonIntentsFormDuplicateUtteranceMessage, {
					items: replaceMultipleWhiteSpace(currentItem.value)
				}),
				index
			);

			return false;
		}

		return true;
	}

	onSaveEntityItem(index: number) {
		this.onEditCommonIntent();
		const currentItem = this.utterances.find((e) => e.index === index);
		const entity = this.commonEntities.find((e) => e.id === this.selectedEntityId);

		if (currentItem) {
			if (!currentItem.isValid) {
				return;
			}

			this.utterances = this.utterances.map((e) => {
				if (e.index === index) {
					const value = replaceMultipleWhiteSpace(this.editUtteranceText) || e.value;
					const isValidEntityName =
						this.selectedEntityId && !CLUValidations.invalidEntityNameRegex.test(value);
					e.value = value;
					e.isInEditMode = false;
					e.isValid = true;
					e.validationMessage = "";
					(e.commonEntityName = isValidEntityName ? entity?.name : ""),
						(e.commonEntityId = isValidEntityName ? entity?.id : undefined);
				}
				return e;
			});
			this.utteranceInvalidMessage = this.validateUtterances();
		}
	}

	markUtteranceInvalid(msg: string, index: number) {
		this.utterances = this.utterances.map((e) => {
			if (e.index === index) {
				e.isValid = false;
				e.validationMessage = msg;
			}
			return e;
		});
	}

	scrollItemsArea() {
		this.$nextTick(() => {
			const items: any = this.$refs.listSection;
			if (items) {
				items.scrollTop = items.scrollHeight;
			}
		});
	}

	showErrorMessage({ error_code, error_description }) {
		switch (error_code) {
			case ServerExceptionCode.TargetHasExistedException:
				MessageHelper.Message(
					NotificationType.Failed,
					this.format(this.locale.CommonIntentsFormNameAlreadyExistMessage)
				);
				break;
			case ServerExceptionCode.CommonIntentNotExistException:
				MessageHelper.Message(
					NotificationType.Failed,
					this.format(this.locale.CommonIntentsFormIntentNotExistMessage)
				);
				break;
			case ServerExceptionCode.CommonUtteranceNotExistException:
				MessageHelper.Message(
					NotificationType.Failed,
					this.format(this.locale.CommonIntentsFormUtterancesRequiredMessage)
				);
				break;
			case ServerExceptionCode.CommonUtteranceDuplicateException:
				MessageHelper.Message(
					NotificationType.Failed,
					this.format(this.locale.CommonIntentsFormDuplicateUtteranceMessage)
				);
				break;
			case ServerExceptionCode.CommonIntentNameLengthException:
				MessageHelper.Message(
					NotificationType.Failed,
					this.format(this.locale.CommonIntentsFormNameLengthMessage, { len: this.nameMaxValue })
				);
				break;
			case ServerExceptionCode.CommonUtteranceLengthException:
				MessageHelper.Message(
					NotificationType.Failed,
					this.format(this.locale.CommonIntentsFormUtteranceLengthMessage, { len: utteranceMaxLen })
				);
				break;
			case ServerExceptionCode.ArgumentIsNullOrInvalidException:
				MessageHelper.Message(
					NotificationType.Failed,
					this.format(this.commonLocale.CommonHttpArgumentIsNullOrInvalid)
				);
				break;
		}
	}

	onRemoveUtterance(index: number) {
		this.utterances = this.utterances.filter((e) => e.index !== index);
		this.utteranceInvalidMessage = this.validateUtterances();
	}

	public deleteUtterence(index: number, value: string) {
		Modal.confirm({
			title: this.$format(IntentLocale.IntentUtteranceDeleteConfirmation, { value }),
			onOk: () => {
				this.onEditCommonIntent();
				this.onRemoveUtterance(index);
			}
		});
	}

	public exitEditMode(id: string) {
		this.utterances = this.utterances.map((f) => ({
			...f,
			isInEditMode: false
		}));
	}

	public cancelEdit(id: string) {
		this.exitEditMode(id);
	}

	onDrawerClose() {
		this.onCommonIntentsFormClose(false);
	}

	validateUtterances(): string {
		//Check Entity Items
		if (this.utterances.length == 0) {
			return this.format(this.locale.CommonIntentsFormUtterancesRequiredMessage);
		}

		const allItems = this.utterances.map((i) => replaceMultipleWhiteSpace(i.value).toLocaleLowerCase().trim());

		const repeatedItems: string[] = [];
		allItems.forEach((i) => {
			const items = allItems.filter((a) => a === i);
			if (items.length > 1 && !repeatedItems.includes(i)) {
				repeatedItems.push(i);
			}
		});

		if (repeatedItems.length > 0) {
			return this.format(this.locale.CommonIntentsFormDuplicateUtteranceMessage, {
				items: repeatedItems.join()
			});
		}

		if (allItems.filter((a) => a.length > patternedUtterancMaxLen && a.includes("{" && "}")).length > 0) {
			return this.format(this.intentLocale.PatternedUtterancesLengthInvalid, {
				len: patternedUtterancMaxLen
			});
		}

		if (allItems.filter((a) => a.length > utteranceMaxLen).length > 0) {
			return this.format(this.locale.CommonIntentsFormUtteranceLengthMessage, {
				len: utteranceMaxLen
			});
		}

		return "";
	}

	validateAndSubmitForm(e: any) {
		this.commonIntentForm.validateFields((err, values) => {
			if (!err) {
				const errorMessage = this.validateUtterances();

				if (errorMessage) {
					MessageHelper.Message(NotificationType.Warning, errorMessage);
					return;
				}

				let current = 1;
				const intentToSave: CommonIntentDetailsModel = {
					id: this.intentId,
					name: replaceMultipleWhiteSpace(values.name),
					commonUtterances: this.utterances.map((e) => {
						const isValidEntityName = !CLUValidations.invalidEntityNameRegex.test(
							replaceMultipleWhiteSpace(e.value)
						);
						const itemDetails: CommonIntentUtteranceModel = {
							id: e.id,
							value: replaceMultipleWhiteSpace(e.value),
							order: current,
							statusId: CommonIntentUtteranceStatus.Active,
							isPattern: false,
							commonEntityName: isValidEntityName ? e.commonEntityName : "",
							commonEntityId: isValidEntityName ? e.commonEntityId : undefined
						};
						current = current + 1;
						return itemDetails;
					})
				};

				this.saveEditIntentData(intentToSave);
			}
		});
	}
}
